import api from '@/lib/api/axios'

export interface LoginAuditRow {
  id: number
  occurredAt: string
  event: string
  outcome: 'success' | 'failure'
  failureReason: string | null
  username: string | null
  userId: number | null
  authMethod: string | null
  /** The enrolled device this row is about (user_devices.id), and its kind and name at the time. */
  deviceId: number | null
  deviceLabel: string | null
  ip: string | null
  forwardedFor: string | null
  browser: string | null
  browserVersion: string | null
  os: string | null
  deviceType: string | null
  country: string | null
  region: string | null
  city: string | null
  geoSource: string | null
  userAgent: string | null
}

export interface LoginAuditPage {
  rows: LoginAuditRow[]
  total: number
  days: number
  limit: number
  offset: number
}

export interface LoginAuditFilters {
  days?: number
  /** An explicit window, ISO timestamps. When either is set, `days` is ignored by the server. */
  from?: string
  to?: string
  outcome?: 'success' | 'failure' | 'all'
  /** One stored reason code, e.g. 'bad_password'. Implies a failure. */
  failureReason?: string
  /** An account number, or all or part of the email that was tried. */
  user?: string
  event?: string
  username?: string
  ip?: string
  limit?: number
  offset?: number
}

export interface Tally { value: string; count: number }

export interface LoginAuditSummary {
  days: number
  success: number
  failure: number
  failureReasons: Tally[]
  topFailedUsernames: Tally[]
  topFailedIps: Tally[]
  countries: Tally[]
  browsers: Tally[]
}

/** Drops empty values and the "all" sentinel so they never reach the query string. */
function params(f: LoginAuditFilters) {
  const out: Record<string, string> = {}
  if (f.days) out.days = String(f.days)
  if (f.from) out.from = f.from
  if (f.to) out.to = f.to
  if (f.outcome && f.outcome !== 'all') out.outcome = f.outcome
  if (f.failureReason) out.failureReason = f.failureReason
  if (f.user) out.user = f.user
  if (f.event) out.event = f.event
  if (f.username) out.username = f.username
  if (f.ip) out.ip = f.ip
  if (f.limit) out.limit = String(f.limit)
  if (f.offset) out.offset = String(f.offset)
  return out
}

export const loginAuditApi = {
  list: (f: LoginAuditFilters) =>
    api.get<{ data: LoginAuditPage }>('/api/login-audit', { params: params(f) })
      .then((r) => r.data.data),

  summary: (days: number) =>
    api.get<{ data: LoginAuditSummary }>('/api/login-audit/summary', { params: { days: String(days) } })
      .then((r) => r.data.data),

  mine: () =>
    api.get<{ data: LoginAuditRow[] }>('/api/login-audit/me')
      .then((r) => r.data.data),
}
